import type { KeyboardEvent } from 'react';

import { Input } from '@/components/core/input';
import { cn } from '@/shared/helpers/index';

import type { EditableColumnEditor } from '../types';
import { CellValidationTooltip } from './cell-validation-tooltip';

type EditableTextInputProps = {
  editor: Exclude<EditableColumnEditor, { type: 'select' }>;
  label: string;
  value: string;
  className?: string;
  validationMessage: string | null;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
};

export function EditableTextInput({
  editor,
  label,
  value,
  className,
  validationMessage,
  onChange,
  onCancel,
  onSubmit,
}: EditableTextInputProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <CellValidationTooltip message={validationMessage}>
      <Input
        autoFocus
        type={editor.type}
        value={value}
        min={editor.type === 'number' ? editor.min : undefined}
        max={editor.type === 'number' ? editor.max : undefined}
        step={editor.type === 'number' ? editor.step : undefined}
        inputMode={editor.type === 'number' ? 'decimal' : undefined}
        placeholder={editor.placeholder}
        aria-label={label}
        aria-invalid={!!validationMessage || undefined}
        className={cn(
          className,
          editor.type === 'number' &&
            '[appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none',
        )}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onSubmit}
        onKeyDown={handleKeyDown}
      />
    </CellValidationTooltip>
  );
}
